import { type Account, type AccountExperience, AccountStatus, type AccountType } from '@openx/types/account';
import { ACL_TYPE, type AclType } from '@openx/types/aclType';
import { type Features, InstanceExperience, InstanceName, type InstanceType } from '@openx/types/instance';
import type { AclFlag, AclMapping } from '@openx/types/session';
import { CORPORATE_SSO_REALMS, type RealUser, type User, UserRole, UserStatus } from '@openx/types/user';

import type { IsAllowedCheck, PermissionsCheck, PermissionsCheckParams } from './useIsAllowed';

export type AreUserAccountTypeAndExperienceValidParams = {
  user?: User;
  accountTypeId?: AccountType;
  experience?: AccountExperience;
};

type HasAccessToInventoryParams = {
  isAllowed: IsAllowedCheck;
  instance: InstanceType;
  accessUser?: User;
};

export const isPD = (user: User): boolean => {
  return user.roles.includes(UserRole.PD);
};

export const isCorporate = (user: User): boolean => {
  return ([InstanceName.CORPORATE, InstanceName.TEST_CORPORATE] as string[]).includes(user.instance_name);
};

export const hasCorporateRole = (user: User): boolean => {
  return user.roles.some(role => role.startsWith('corporate'));
};

export const isAdmin = (user: User): boolean => {
  return user.roles.includes(UserRole.ROOT) || user.roles.includes(UserRole.ADMIN);
};

export const isAllowedByAcl = (aclList: AclFlag[], aclMapping: AclMapping, acl: string): boolean => {
  const aclIndex = aclMapping[acl];
  const relatedAclFlag = aclList[aclIndex];

  return relatedAclFlag === 1;
};

export const isAllowedByCompiledAcl =
  (acl: AclType): PermissionsCheck =>
  ({ aclList, aclMapping = {} }: PermissionsCheckParams): boolean => {
    return isAllowedByAcl(aclList || [], aclMapping, acl);
  };

export const isAllowedToViewPublishersPage =
  (acl: AclType): PermissionsCheck =>
  ({ aclList, aclMapping = {}, accessUser }: PermissionsCheckParams): boolean => {
    return isAllowedByAcl(aclList || [], aclMapping, acl) && !isReportingWithTermsUpdateOnly(accessUser);
  };

export const isUserAllowedByAcl =
  (acl: AclType): PermissionsCheck =>
  ({ userAcl, aclMapping = {} }: PermissionsCheckParams): boolean => {
    return isAllowedByAcl(userAcl || [], aclMapping, acl);
  };

export const isUserAllowedByAnyAcl =
  (acl: AclType[]): PermissionsCheck =>
  ({ userAcl, aclMapping = {} }: PermissionsCheckParams): boolean => {
    return acl.some(singleAcl => isAllowedByAcl(userAcl || [], aclMapping, singleAcl));
  };

export const isAccountAllowedByAcl =
  (acl: AclType): PermissionsCheck =>
  ({ accountAcl = [], aclMapping = {} }: PermissionsCheckParams): boolean =>
    isAllowedByAcl(accountAcl, aclMapping, acl);

export const isUserActive = (user: User): boolean => {
  return user.status === UserStatus.ACTIVE;
};

export const isAccountActive = (account: Account): boolean => {
  return account.status === AccountStatus.ACTIVE;
};

export const isAllowedBySomeCompiledAcls =
  (acls: AclType[]): PermissionsCheck =>
  ({ aclList, aclMapping = {} }: PermissionsCheckParams): boolean =>
    acls.some(acl => isAllowedByAcl(aclList || [], aclMapping, acl));

export const isMasterNetwork = (accessAccount: PermissionsCheckParams['accessAccount']): boolean => {
  if (!accessAccount) return false;

  return !accessAccount.account_uid;
};

export const isCrtRoleOnly = (user?: User): boolean => {
  return user?.roles?.length === 1 && user.roles[0] === UserRole.CRT_ACCESS;
};

export const isReportingWithTermsUpdateOnly = (user?: User): boolean => {
  if (user?.roles?.length !== 2) return false;

  const requiredRoles = [UserRole.PUBLISHER_TC_ACCESS, UserRole.PUBLISHER_REPORTING];

  return requiredRoles.every(role => user.roles.includes(role));
};

const isCorporateUser = (user?: RealUser): boolean => CORPORATE_SSO_REALMS.includes(user?.realm ?? '');

export const isInternalUser = ({ accessAccount, realUser }: PermissionsCheckParams): boolean =>
  isCorporateUser(realUser) && isMasterNetwork(accessAccount);

export const isBefOperator = (instance?: InstanceType): boolean => {
  return Number(instance?.buyer_experience) === 1;
};

export const exchangeIsOn = (instance: InstanceType): boolean => {
  return isBefOperator(instance) && !featureIsOn(instance, 'cross_promotion');
};

export const featureIsOn = (instance: InstanceType, feature: keyof Features): boolean => {
  return !!Number(instance.features?.[feature]);
};

export const hasAccessToTrafficSets = (isAllowed: IsAllowedCheck, instance?: InstanceType): boolean => {
  return (
    isBefOperator(instance) &&
    isAllowed(isUserAllowedByAcl(ACL_TYPE.LINEITEM_READ)) &&
    isAllowed(isUserAllowedByAcl(ACL_TYPE.ORDER_READ)) &&
    isAllowed(isUserAllowedByAcl(ACL_TYPE.ADVERTISER_READ))
  );
};

export const hasAccessToInventory = ({ isAllowed, instance, accessUser }: HasAccessToInventoryParams) => {
  if (!accessUser) {
    return false;
  }

  // it checks if instance experience is 'bef' - note: is not the same as isBefOperator()
  if (instance.experience === InstanceExperience.BEF) {
    const isRootUserAndCanAccessGlobalDeals = isAdmin(accessUser) && featureIsOn(instance, 'pmp_has_global_deals');
    const hasCorrectPermissions =
      isAllowed(isUserAllowedByAcl(ACL_TYPE.SITE_READ)) && isAllowed(isUserAllowedByAcl(ACL_TYPE.PUBLISHER_READ));

    return isRootUserAndCanAccessGlobalDeals || (hasCorrectPermissions && !exchangeIsOn(instance));
  } else {
    return isAllowed(isAllowedByCompiledAcl(ACL_TYPE.PUBLISHER_READ));
  }
};
